import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Badge, Button } from 'react-bootstrap'
import { UserContext } from 'shared/users/contexts/UserContext'
import FormRow, { IAddressForm } from '../../../components/FormRow'
import NotificationEmailsField from '../../../components/Form/NotificationEmailsField'
import { TitleBar } from '../../../components/TitleBar'
import useCountries from '../../../hooks/useCountries'
import useCurrentCustomer from '../hooks/useCurrentCustomer'
import CustomerService from '../services/customer.service'
import UsersTable from '../UsersTable'
import { NotificationContext } from '../../../context/NotificationContext'
import ClientApiTokenModal from './ClientApiTokenModal'
import CustomsProfileTable from '../CustomsProfileTable'

function CustomerAdmin() {
  const {
    setCustomer,
    customer,
  } = useCurrentCustomer()
  const [isApiTokenModalVisible, setIsApiTokenModalVisible] = useState<boolean>(false)
  const { contextHeaderNotificationMessage } = useContext(NotificationContext)

  const { user } = useContext(UserContext)
  const [loading, setLoading] = useState<boolean>(false)

  const {
    t,
    i18n,
  } = useTranslation()
  const { countries } = useCountries(i18n.language)

  const saveCustomer = () => {
    setLoading(true)
    CustomerService.putCurrentCustomer(customer)
      .then((savedCustomer) => {
        setCustomer(savedCustomer)
        setTimeout(() => {
          toast.success(t('messages.savingSuccess'))
          setLoading(false)
        }, 100)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const loadingSpinner = () => (<i className="fal fa-spinner fa-spin" />)

  return (
    <>
      <TitleBar title={t('customers.customer', { context: ('edit') })}>
        <div className="d-flex justify-content-end align-items-center ps-3 w-100">
          <button
            type="button"
            className="btn btn-secondary btn-lg d-flex align-items-center text-primary shadow-sm"
            onClick={saveCustomer}
          >
            {
              loading && loadingSpinner()
            }
            {
              !loading && (
                <span>{t('buttons.save')}</span>
              )
            }
          </button>
        </div>
      </TitleBar>
      <div
        className={`new-customer__container overflow-auto ${contextHeaderNotificationMessage !== '' ? ' customer__container--notification-open' : ''}`}
      >
        <div className="pt-4 px-4 col-12 col-xl-4 mb-5 mb-md-0">
          <div className="heading">
            <h3>
              {t('customers.companyInfo')}
            </h3>
          </div>
          <FormRow title={t('common.name', { context: 'capitalized' })}>
            <input
              className="form-control"
              type="text"
              placeholder={t('common.fieldPlaceholder')}
              value={customer?.name}
              onChange={(event) => {
                setCustomer({
                  ...customer,
                  name: event.target.value,
                })
              }}
            />
          </FormRow>
          <FormRow title={t('customers.registryCode')}>
            <input
              className="form-control"
              type="text"
              placeholder={t('common.fieldPlaceholder')}
              value={customer?.registryCode}
              disabled={user?.role !== 'ADMIN'}
              onChange={(event) => {
                setCustomer({
                  ...customer,
                  registryCode: event.target.value,
                })
              }}
            />
          </FormRow>
          <FormRow title={t('common.email')}>
            <input
              className="form-control"
              type="text"
              placeholder={t('common.fieldPlaceholder')}
              value={customer?.contactEmail}
              onChange={(event) => {
                setCustomer({
                  ...customer,
                  contactEmail: event.target.value,
                })
              }}
            />
          </FormRow>
          <FormRow
            title={t('customers.address')}
            inputType="address"
            countriesOptions={countries}
            addressForm={{
              street: customer?.street ?? '',
              streetNumber: customer?.streetNumber ?? '',
              city: customer?.city ?? '',
              country: customer?.country ?? '',
              zip: customer?.zipCode ?? '',
            }}
            setAddressForm={(form: IAddressForm) => {
              setCustomer({
                ...customer,
                street: form.street,
                streetNumber: form.streetNumber,
                city: form.city,
                country: form.country,
                zipCode: form.zip,
              })
            }}
          />
          {customer?.transitStatusNotificationEnabled && (
            <NotificationEmailsField
              value={customer?.notificationEmails ?? []}
              onChange={(emails) => {
                setCustomer({
                  ...customer,
                  notificationEmails: emails,
                })
              }}
            />
          )}
          <FormRow title={t('customers.apiTokenAccess')}>
            {
              customer.clientApiAccess !== true && (
                <Badge bg="danger" className="mt-2">{t('customers.apiTokenAccessForbidden')}</Badge>
              )
            }
            {
              customer.clientApiAccess === true && (
                <Button
                  type="button"
                  onClick={() => {
                    setIsApiTokenModalVisible(true)
                  }}
                >
                  <span>
                    {t('customers.apiTokenActions')}
                  </span>
                </Button>
              )
            }

          </FormRow>

        </div>
        {
          customer?.id !== null && customer.id !== undefined
          && (
            <>
              <div className="pt-4 pe-4 col-12 col-xl-8 mb-5 mb-md-0">
                <UsersTable customerId={customer.id} />
                <CustomsProfileTable customerId={customer.id} />
              </div>
              {customer.clientApiAccess === true && (
                <ClientApiTokenModal
                  isVisible={isApiTokenModalVisible}
                  toggleVisibility={() => setIsApiTokenModalVisible(!isApiTokenModalVisible)}
                />
              )}

            </>
          )
        }
      </div>
    </>
  )
}

export default CustomerAdmin
